import outdent from "outdent";
import logo from "../microsoftGraph/logo.svg";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";

const environments = {
  global: {
    loginDomain: "login.microsoftonline.com",
    graphDomain: "graph.microsoft.com",
  },
  "usgov-l4": {
    loginDomain: "login.microsoftonline.us",
    graphDomain: "graph.microsoft.us",
  },
  "usgov-l5": {
    loginDomain: "login.microsoftonline.us",
    graphDomain: "dod-graph.microsoft.us",
  },
  china: {
    loginDomain: "login.chinacloudapi.cn",
    graphDomain: "microsoftgraph.chinacloudapi.cn",
  },
} as const;
type Environment = keyof typeof environments;

const environmentConfig = (environment: string | undefined) =>
  environments[(environment as Environment) || "global"];

export const microsoftGraphDelegated = defineConnector({
  identifier: "microsoftGraphDelegated",
  name: "Microsoft Graph (delegated)",
  tagLine: "Microsoft 365 API as a user",
  logo,
  protocol: "http",
  tags: ["productivity", "featured"],
  defaultUrlPattern: ({ environment }) =>
    `^https://${environmentConfig(environment).graphDomain.replaceAll(".", "\\.")}/.*`,
  auth: oauthAuth,
  steps: [
    {
      title: "Create an Azure app registration",
      items: [
        {
          kind: "markdown",
          content: outdent`
            This connect flow uses the authorization code grant, which allows the app to act on behalf of a signed-in user with delegated permissions.

            1. Navigate to your [Azure app registrations](https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade) and select **New Registration**
            2. **Name**: Give your app a descriptive name
            3. **Supported account types**: Select the account types appropriate for your organization
            4. **Redirect URI**: Select **Web** and enter the callback URL shown below
            5. Select **Register**
          `,
        },
        {
          kind: "copyable",
          label: "Callback URL",
          value:
            "{API_URL}/connected-apps/oauth/microsoftGraphDelegated/callback",
        },
      ],
    },
    {
      title: "Get client ID and tenant ID",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Navigate to the **Overview** of your created app and copy the following values:
          `,
        },
        {
          kind: "input",
          label: "Application (client) ID",
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Directory (tenant) ID",
          target: { kind: "configuration", field: "tenantId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
      ],
    },
    {
      title: "Create client secret",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Select **Add a certificate or secret** from the overview page, then select **+ New client secret**
            2. Enter a description for the client secret and select your preferred expires setting
            3. Select **Add**
            4. Copy the **value** of the secret
          `,
        },
        {
          kind: "input",
          label: "Client secret value",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Add API permissions",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to **API permissions** and select **+ Add a permission**
            2. Select the **Microsoft Graph** API
            3. Select **Delegated permissions**
            4. Select your desired permissions
            5. Select **Add permissions**

            Add the scopes you selected as a space-separated list below. \`openid\` and \`offline_access\` are required.
          `,
        },
        {
          kind: "input",
          label: "Scopes",
          defaultValue: "openid offline_access User.Read",
          helperText:
            "Space-separated list. Must include openid and offline_access.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Specify your environment",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Microsoft has different domains for different deployments. [Learn more…](https://learn.microsoft.com/en-us/graph/deployments#microsoft-graph-and-graph-explorer-service-root-endpoints)
          `,
        },
        {
          kind: "select",
          label: "Environment",
          target: { kind: "configuration", field: "environment" },
          options: [
            {
              label: "Microsoft Graph global service",
              value: "global",
            },
            {
              label: "Microsoft Graph for US Government L4",
              value: "usgov-l4",
            },
            {
              label: "Microsoft Graph for US Government L5 (DOD)",
              value: "usgov-l5",
            },
            {
              label: "Microsoft Graph China operated by 21Vianet",
              value: "china",
            },
          ],
          defaultValue: "global",
        },
      ],
    },
    {
      title: "Authorize your Microsoft account",
      items: [
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
            { kind: "configuration", field: "tenantId" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: ({ environment, tenantId }) =>
      `https://${environmentConfig(environment).loginDomain}/${tenantId}/oauth2/v2.0/authorize`,
    tokenEndpoint: ({ environment, tenantId }) =>
      `https://${environmentConfig(environment).loginDomain}/${tenantId}/oauth2/v2.0/token`,
    issuer: ({ environment, tenantId }) =>
      `https://${environmentConfig(environment).loginDomain}/${tenantId}/v2.0`,
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ environment }) =>
      `https://${environmentConfig(environment).graphDomain}/v1.0/me`,
  },
});
